<div class="p-3 md:p-6 h-full">
  <!-- Loading State -->
  <div *ngIf="loading$ | async" class="flex justify-around items-center h-full">
    <svg class="animate-spin h-6 w-6 text-blue-royal" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  </div>

  <!-- Error State -->
  <div *ngIf="(error$ | async) && !(loading$ | async)" class="flex items-center justify-center h-full">
    <div class="text-center">
      <p class="text-red-600 dark:text-red-400 mb-4">{{ error$ | async }}</p>
      <button (click)="goBack()" class="text-blue-600 dark:text-blue-400 hover:underline">
        Go back to workspaces
      </button>
    </div>
  </div>

  <!-- Workspace Content -->
  <div *ngIf="workspace && !(loading$ | async)" class="h-full grid grid-cols-3 gap-6">

    <!-- Left Side - Workspace List -->
    <div class="col-span-3 lg:col-span-2 bg-white dark:bg-indigo-custom rounded-xl flex flex-col h-full overflow-hidden">

      <!-- Workspace Reports Component -->
      <app-workspace-reports [workspaceId]="workspaceId" [workspace]="workspace"></app-workspace-reports>

    </div>

    <!-- Right Side - Workspace Details -->
    <div class="col-span-3 lg:col-span-1 bg-gray-50 dark:bg-indigo-custom rounded-xl p-3 md:p-3 flex flex-col h-full overflow-hidden relative">

      <!-- Header with Icon and Edit/Delete Links -->
      <div class="flex items-center justify-between p-3 pb-0">
        <div class="flex items-center gap-3">
          <div [class]="'w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-lg bg-violet-500 shadow shadow-violet-200'">
            {{ workspace.initials }}
          </div>
        </div>
        <div class="flex items-center gap-1">
          <button type="button"
                  (click)="editWorkspace()"
                  class="px-2.5 py-1 text-sm font-medium rounded-lg text-gray-500 dark:text-gray-400 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700">
            Edit workspace
          </button>
          <button *ngIf="!workspace.is_default"
                  type="button"
                  (click)="deleteWorkspace()"
                  class="px-2.5 py-1 text-sm font-medium rounded-lg text-gray-500 dark:text-gray-400 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700">
            Delete workspace
          </button>
        </div>
      </div>

      <!-- Workspace Title - Sticky -->
      <div class="px-3 pb-3  sticky top-0 z-10 mt-3">
        <div class="mb-4">
          <h1 class="text-xl font-bold text-gray-900 dark:text-white">{{ workspace.name }}</h1>
          <span *ngIf="workspace.icp" class="text-xs bg-violet-500/10 text-violet-500 py-1 px-2 rounded-md">ICP: {{ workspace.icp.name }}</span>
        </div>
        <p class="text-xs text-gray-600 dark:text-gray-400 flex items-center space-x-2 mb-2">
          <span>{{ workspace.searches_count }} searches</span>
          <span class="text-[8px] text-gray-400">•</span>
          <span>Created {{ workspace.created_at | date:'medium' }}</span>
          <span class="text-[8px] text-gray-400">•</span>
          <span>{{ loadingFiles ? workspace.total_files : workspaceFiles.length }} files attached</span>
        </p>
      </div>

      <!-- Scrollable Content Container -->
      <div class="flex-1 overflow-y-auto custom-scroll px-3 pb-3">
        <!-- Description Section -->
        <div class="mb-6" *ngIf="workspace.description">
          <h3 class="text-sm font-semibold text-gray-700 dark:text-white mb-2">Description:</h3>
          <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {{ workspace.description }}
          </p>
        </div>

        <!-- Instructions Section -->
        <div class="mb-6" *ngIf="workspace.instructions">
          <h3 class="text-sm font-semibold text-gray-700 dark:text-white mb-2">Instructions:</h3>
          <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {{ workspace.instructions }}
          </p>
        </div>

        <!-- Knowledge Base Section -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-lg font-semibold text-gray-700 dark:text-white">Knowledge Base</h3>
            <span class="text-sm text-gray-500 dark:text-gray-400">{{ formattedUsedSize }} / {{ formattedMaxSize }}</span>
          </div>

          <!-- Progress Bar -->
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
            <div class="h-2 rounded-full transition-all duration-300"
                 [ngClass]="isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-violet-500'"
                 [style.width.%]="knowledgeBaseUsage"></div>
          </div>

          <!-- Storage warning banner -->
          <div *ngIf="isAtLimit" class="flex items-center gap-2 p-3 mb-4 text-sm text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg">
            <svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd"/>
            </svg>
            <span>Storage limit reached. Delete files to free up space.</span>
          </div>
          <div *ngIf="isNearLimit && !isAtLimit" class="flex items-center gap-2 p-3 mb-4 text-sm text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400 rounded-lg">
            <svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
            </svg>
            <span>You're approaching the {{ formattedMaxSize }} storage limit ({{ knowledgeBaseUsage }}% used).</span>
          </div>

          <!-- Files List -->
          <div class="space-y-3">
            <!-- Loading state for files -->
            <div *ngIf="loadingFiles" class="flex justify-center py-8">
              <svg class="animate-spin h-6 w-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>

            <!-- Files -->
            <div *ngIf="!loadingFiles && workspaceFiles.length > 0">

              <div *ngFor="let file of workspaceFiles" class="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-800 rounded-lg transition-colors mb-3">
                <div class="flex items-center gap-3 min-w-0">
                  <!-- File Icon -->
                  <div class="flex-shrink-0">
                    <div class="w-10 h-10 bg-white dark:bg-gray-700 rounded-lg flex items-center justify-center">
                      <svg *ngIf="getFileType(file) === 'pdf'" class="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                        <text x="12" y="18" text-anchor="middle" font-family="Arial, sans-serif" font-size="6" font-weight="700" fill="currentColor">PDF</text>
                      </svg>
                      <svg *ngIf="getFileType(file) === 'xlsx'" class="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12.9,14.5L15.8,19H14L12,15.6L10,19H8.2L11.1,14.5L8.2,10H10L12,13.4L14,10H15.8L12.9,14.5Z"/>
                      </svg>
                      <svg *ngIf="getFileType(file) === 'pptx'" class="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6,2H14L20,8V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V4A2,2 0 0,1 6,2M13,3.5V9H18.5L13,3.5M8,11V13H9V19H8V20H12V19H11V17H13A3,3 0 0,0 16,14A3,3 0 0,0 13,11H8M13,13A1,1 0 0,1 14,14A1,1 0 0,1 13,15H11V13H13Z"/>
                      </svg>
                      <svg *ngIf="getFileType(file) === 'image'" class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M21,3H3C2,3 1,4 1,5V19A2,2 0 0,0 3,21H21C22,21 23,20 23,19V5C23,4 22,3 21,3M5,17L8.5,12.5L11,15.5L14.5,11L19,17H5Z"/>
                      </svg>
                      <svg *ngIf="getFileType(file) === 'document'" class="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M13,3.5L18.5,9H13V3.5Z"/>
                      </svg>
                    </div>
                  </div>

                  <!-- File Info -->
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-gray-700 dark:text-white truncate">{{ file.original_filename }}</p>
                    <div class="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                      <span>{{ file.formatted_file_size }}</span>
                      <span>•</span>
                      <span>Uploaded {{ getFileUpdatedAt(file) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="flex items-center gap-2 flex-shrink-0">
                  <!-- View Button (for PDFs and images) -->
                  <button *ngIf="canViewFile(file)"
                          (click)="viewFile(file)"
                          class="p-2 text-gray-500 dark:text-gray-400 transition-colors rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
                          title="View file">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                    </svg>
                  </button>

                  <!-- Download Button -->
                  <button (click)="downloadFile(file)"
                          class="p-2 text-gray-500 dark:text-gray-400 transition-colors rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
                          title="Download file">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"></path>
                    </svg>
                  </button>

                  <!-- Delete Button (always visible) -->
                  <button (click)="deleteFile(file)"
                          class="p-2 text-gray-500 dark:text-gray-400 transition-colors rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
                          title="Delete file">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Empty Files State -->
            <div *ngIf="!loadingFiles && workspaceFiles.length === 0" class="text-center py-8">
              <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
              </svg>
              <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">No files uploaded yet</p>
              <p class="text-xs text-gray-500 dark:text-gray-500">Upload files to build your knowledge base</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</div>

<!-- Edit Workspace Modal -->
<app-edit-workspace
  *ngIf="showEditModal"
  [workspace]="workspace"
  [saving]="editSaving"
  (close)="closeEditModal()"
  (save)="saveWorkspace($event)"
  (filesUpdated)="onFilesUpdated()">
</app-edit-workspace>

<!-- Delete File Modal -->
<app-delete-workspace-modal
  *ngIf="deleteFileState.show"
  [fileName]="deleteFileState.file?.original_filename || ''"
  [isDeleting]="deleteFileState.isDeleting"
  (confirmDelete)="confirmDeleteFile()"
  (cancelDelete)="cancelDeleteFile()"
></app-delete-workspace-modal>

<!-- Delete Workspace Modal -->
<app-delete-workspace-modal
  *ngIf="deleteWorkspaceState.show"
  [fileName]="workspace?.name || ''"
  [title]="'Delete workspace'"
  [isDeleting]="deleteWorkspaceState.isDeleting"
  (confirmDelete)="confirmDeleteWorkspace()"
  (cancelDelete)="cancelDeleteWorkspace()"
></app-delete-workspace-modal>
